import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getSubjectPriority, listSubjects } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// "Điểm ưu tiên xếp môn theo khối" — hiện ĐÚNG con số CP-SAT dùng để quyết
// định môn nào bị cắt giảm trước khi thiếu tiết: điểm càng CAO càng được ưu
// tiên giữ nguyên (Văn/Toán luôn cao nhất, Tiếng anh luôn thấp nhất). Điểm
// = bậc cố định của môn (SUBJECT_PRIORITY_ORDER, cách nhau 10đ) + độ khít GV
// riêng từng lớp (1-3đ, không bao giờ đủ để nhảy bậc môn khác).
const khoiOf = (className) => {
  const m = String(className || "").match(/^(\d{1,2})A/);
  return m ? m[1] : "?";
};

const HappySchoolSubjectPriority = ({ weekId }) => {
  const schoolId = getActiveSchoolId();
  const [classes, setClasses] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!schoolId || !weekId) return;
    setLoading(true);
    setError("");
    Promise.all([getSubjectPriority(schoolId, weekId), listSubjects(schoolId)])
      .then(([pr, s]) => {
        setClasses(pr.classes || []);
        setSubjects(s || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, weekId]);

  const subjectsById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s.name])), [subjects]);

  // khoi -> { classes: [...], subjectIds: [...sap theo diem TB giam dan] }
  const byKhoi = useMemo(() => {
    if (!classes) return {};
    const groups = {};
    classes.forEach((c) => {
      const k = khoiOf(c.name);
      groups[k] = groups[k] || { classes: [], subjectTotals: {}, subjectCounts: {} };
      groups[k].classes.push(c);
      c.subjects.forEach((s) => {
        groups[k].subjectTotals[s.subjectId] = (groups[k].subjectTotals[s.subjectId] || 0) + s.priority;
        groups[k].subjectCounts[s.subjectId] = (groups[k].subjectCounts[s.subjectId] || 0) + 1;
      });
    });
    Object.values(groups).forEach((g) => {
      g.classes.sort((a, b) => a.name.localeCompare(b.name, "vi", { numeric: true }));
      g.subjectIds = Object.keys(g.subjectTotals).sort(
        (a, b) => g.subjectTotals[b] / g.subjectCounts[b] - g.subjectTotals[a] / g.subjectCounts[a]
      );
    });
    return groups;
  }, [classes]);

  if (!schoolId) {
    return <div style={{ padding: 16 }}>Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link>.</div>;
  }
  if (!weekId) return <p style={{ color: "#6b7280" }}>Chưa chọn tuần TKB.</p>;
  if (loading && classes === null) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;

  const khoiOrder = Object.keys(byKhoi).sort((a, b) => Number(a) - Number(b));

  return (
    <div>
      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
        Điểm ưu tiên môn (lớp-môn) khi xếp lịch — <strong>cao hơn = được giữ nguyên trước</strong> khi thiếu tiết
        phải cắt giảm ai đó. Cách nhau ≥10đ là khác BẬC môn (Văn &gt; Toán &gt; Công nghệ &gt; Lịch sử &gt; Địa lý &gt;
        Vật lý &gt; GDKT&amp;PL &gt; Hoá &gt; Sinh &gt; Sinh hoạt &gt; Tiếng anh); lệch trong cùng 1-3đ là do độ khít
        lịch GV riêng từng lớp (không bao giờ đủ để vượt bậc môn khác).
      </p>
      {khoiOrder.map((khoi) => {
        const g = byKhoi[khoi];
        return (
          <div key={khoi} style={{ marginBottom: 28 }}>
            <h4 style={{ margin: "0 0 8px" }}>Khối {khoi}</h4>
            <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ ...thFix(0), zIndex: 6, minWidth: 90 }}>Lớp</th>
                    {g.subjectIds.map((sid) => (
                      <th key={sid} style={{ ...th, minWidth: 90 }}>{subjectsById[sid] || sid}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.classes.map((c) => {
                    const bySubj = Object.fromEntries(c.subjects.map((s) => [s.subjectId, s]));
                    return (
                      <tr key={c.id}>
                        <td style={{ ...td, position: "sticky", left: 0, zIndex: 5, background: "#fff", fontWeight: 700, textAlign: "left" }}>{c.name}</td>
                        {g.subjectIds.map((sid) => {
                          const s = bySubj[sid];
                          return (
                            <td key={sid} style={{ ...td, background: s ? "#eff6ff" : "#f9fafb", color: s ? "#1d4ed8" : "#d1d5db", fontWeight: s ? 700 : 400 }} title={s ? `${s.periodsPerWeek} tiết/tuần${s.tightForClass ? " • GV khít buổi lớp này" : ""}` : ""}>
                              {s ? s.priority : "—"}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const th = { border: "1px solid #e5e7eb", padding: "4px 6px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center", position: "sticky" };
const thFix = (left) => ({ ...th, position: "sticky", left, top: 0, zIndex: 4, background: "#f3f4f6" });
const td = { border: "1px solid #e5e7eb", padding: "3px 5px", textAlign: "center", verticalAlign: "top" };

export default HappySchoolSubjectPriority;
